import gsap from 'gsap'
import type { Directive, DirectiveBinding } from 'vue'

// Extended HTMLElement interface for drag-related properties
export interface DragElement extends HTMLElement {
  activeIndex?: Ref<number> // Reactive current active item index
  amountPages?: Ref<number> // Reactive total pages based on visible items
  scrollToItem?: (index: number) => void // Function to scroll to a specific item
  _dragHandlers?: {
    pointerdown: (e: PointerEvent) => void
    pointermove: (e: PointerEvent) => void
    pointerup: (e: PointerEvent) => void
    pointerleave: () => void
    click: (e: MouseEvent) => void
    scroll: () => void
    keydown: (e: KeyboardEvent) => void
    resize: () => void
  }
}

// Binding value interface for directive options
interface BindingValue {
  onScrollTo?: (fn: (index: number) => void) => void // Callback to receive scrollToItem function
  onSnapTargets?: (targets: number[]) => void // Callback to receive initial collapsed snap targets
  onActiveIndexChange?: (index: number) => void // Callback on active index change
  onScroll?: (scrollLeft: number) => void // Called on every scroll position change
  scrollToLast?: boolean // Add end spacer so the last item can be scrolled to the active (snap) position
}

const dragScroll: Directive = {
  mounted(el: DragElement, binding: DirectiveBinding<BindingValue>) {
    if (el.children.length < 2) return // Exit if fewer than 2 children

    // All snapping is handled by GSAP — no CSS snap to avoid browser conflicts.
    // overflow-anchor: none prevents the browser from adjusting scrollLeft when
    // content before the viewport changes size (e.g. item 0 collapsing on swipe).
    el.classList.add('overflow-x-auto', 'hide-scrollbar')
    el.style.overflowAnchor = 'none'

    // Binding options
    const onScrollTo = binding.value?.onScrollTo
    const onSnapTargets = binding.value?.onSnapTargets
    const onActiveIndexChange = binding.value?.onActiveIndexChange
    const onScroll = binding.value?.onScroll
    const scrollToLast = binding.value?.scrollToLast ?? false

    // Reactive active index
    const activeIndex = ref(0)
    el.activeIndex = activeIndex

    // Reactive amount pages
    const amountPages = ref(1)
    el.amountPages = amountPages

    // Optional end spacer element — makes the last item scrollable to the active (snap) position
    let spacerEl: HTMLElement | null = null

    // Function to calculate items per view and amount pages
    function calculateReachableSnapPoints() {
      const children = Array.from(el.children).filter(c => c !== spacerEl) as HTMLElement[]

      if (!children.length) {
        amountPages.value = 1
        return
      }

      let snapPoints = 1

      // Compute container styles once to avoid repeated layout recalculation
      const containerStyle = getComputedStyle(el)
      const containerWidth = el.clientWidth - parseFloat(containerStyle.paddingLeft) - parseFloat(containerStyle.paddingRight)
      const columnGap = parseFloat(containerStyle.columnGap) || 0

      // Helper function to calculate item width including margins and gap
      function calculateItemWidth(child: HTMLElement, isLast: boolean): number {
        const childStyle = getComputedStyle(child)
        const marginLeft = parseFloat(childStyle.marginLeft) || 0
        const marginRight = parseFloat(childStyle.marginRight) || 0
        const childWidth = child.offsetWidth + marginLeft + marginRight
        return isLast ? childWidth : childWidth + columnGap
      }

      // Iterate through children to calculate reachable snap points
      for (const [index, child] of children.entries()) {
        const currentChildWidth = calculateItemWidth(child, index >= children.length - 1)

        // Calculate the width of all remaining children
        const sliced = children.slice(index + 1)
        const remainingWidth = sliced.reduce((total, nextChild, i) => {
          return total + calculateItemWidth(nextChild, i === sliced.length - 1)
        }, 0)

        if (remainingWidth > containerWidth) {
          // As long as the remaining children can fill the container, the snap point is reachable
          snapPoints++
        } else {
          if (currentChildWidth + remainingWidth > containerWidth) {
            // Then it can not reach the next snap point but still overflows so we add one more
            snapPoints++
          }
          break
        }
      }

      amountPages.value = snapPoints || 1
    }

    // Cached snap targets — computed once and on resize to avoid getComputedStyle on every scroll
    let snapTargets: number[] = []

    const recomputeSnapTargets = () => {
      const style = getComputedStyle(el)
      const inset = parseFloat(style.scrollPaddingLeft) || parseFloat(style.paddingLeft) || 0
      snapTargets = Array.from(el.children)
        .filter(c => c !== spacerEl)
        .map(c => Math.max(0, (c as HTMLElement).offsetLeft - inset))
    }

    // Inserts/updates an invisible spacer at the end so the last snap target is reachable.
    // Measures the last child's offsetLeft directly from the DOM rather than using cached
    // snapTargets, because items may be expanded when this runs (e.g. item 0 at progress=1),
    // which inflates scrollWidth without updating snapTargets — causing an undersized spacer.
    const updateEndSpacer = () => {
      const children = Array.from(el.children).filter(c => c !== spacerEl) as HTMLElement[]
      const lastChild = children[children.length - 1]
      if (!lastChild) return
      const style = getComputedStyle(el)
      const inset = parseFloat(style.scrollPaddingLeft) || parseFloat(style.paddingLeft) || 0
      const lastSnap = Math.max(0, lastChild.offsetLeft - inset)
      const extra = Math.max(0, lastSnap - (el.scrollWidth - el.clientWidth - 1))
      if (!spacerEl) {
        spacerEl = document.createElement('div')
        spacerEl.setAttribute('data-drag-scroll-spacer', '')
        spacerEl.setAttribute('aria-hidden', 'true')
        spacerEl.style.cssText = 'flex-shrink: 0; pointer-events: none;'
        el.appendChild(spacerEl)
      }
      spacerEl.style.width = extra > 0 ? `${extra}px` : '0px'
    }

    // Utility: Update active index by comparing scrollLeft against snap targets (inset-corrected)
    const updateActiveIndex = () => {
      if (!snapTargets.length) return

      // Handle full scroll to right: set to last page (tolerance for precision)
      if (Math.abs(el.scrollLeft + el.clientWidth - el.scrollWidth) <= 1) {
        const last = amountPages.value - 1
        if (activeIndex.value !== last) {
          activeIndex.value = last
          onActiveIndexChange?.(last)
        }
        return
      }

      const { scrollLeft } = el
      let minDistance = Infinity
      let currentIndex = 0

      snapTargets.forEach((target, index) => {
        const distance = Math.abs(target - scrollLeft)
        if (distance < minDistance) {
          minDistance = distance
          currentIndex = index
        }
      })

      if (activeIndex.value !== currentIndex) {
        activeIndex.value = currentIndex
        onActiveIndexChange?.(currentIndex)
      }
    }

    let isProgrammaticScroll = false

    // Animate to snap target using GSAP
    const snapTo = (fromVelocity: number = 0) => {
      if (!snapTargets.length) return

      // Project where inertia would land, then snap to nearest target
      const projected = el.scrollLeft + fromVelocity * 0.12
      const target = snapTargets.reduce((prev: number, curr: number) =>
        Math.abs(curr - projected) < Math.abs(prev - projected) ? curr : prev)

      isProgrammaticScroll = true
      gsap.to(el, {
        scrollLeft: target,
        duration: 0.5,
        ease: 'power3.out',
        overwrite: true,
        onUpdate: () => onScroll?.(el.scrollLeft),
        onComplete: () => {
          isProgrammaticScroll = false
          updateActiveIndex()
        },
      })
    }

    // Utility: Scroll to specific item using GSAP
    const scrollToItem = (index: number) => {
      const target = snapTargets[index]
      if (target === undefined) return
      if (activeIndex.value !== index) {
        activeIndex.value = index
        onActiveIndexChange?.(index)
      }
      isProgrammaticScroll = true
      gsap.to(el, {
        scrollLeft: target,
        duration: 0.6,
        ease: 'power2.out',
        overwrite: true,
        onUpdate: () => onScroll?.(el.scrollLeft),
        onComplete: () => { isProgrammaticScroll = false },
      })
    }

    // Attach scrollToItem to element and provide via callback
    el.scrollToItem = scrollToItem
    onScrollTo?.(scrollToItem)

    // Initial calculation — snap targets emitted before any item expands (collapsed positions),
    // but amountPages and end spacer deferred to nextTick so they measure after item 0 expands.
    recomputeSnapTargets()
    onSnapTargets?.(snapTargets)
    nextTick(() => {
      if (scrollToLast) {
        // Spacer guarantees all snap targets are reachable — no need to compute
        updateEndSpacer()
        amountPages.value = snapTargets.length
      } else {
        calculateReachableSnapPoints()
      }
    })

    // --- Pointer drag handling ---
    // Uses pointer capture for reliable drag across fast movements.
    // CSS snap is removed during drag so the browser doesn't fight the drag,
    // then GSAP animates to the nearest snap target on release.

    let isDragging = false
    let wasDragged = false
    let startX = 0
    let startScroll = 0
    let lastX = 0
    let lastTime = 0
    let velocity = 0 // px/ms

    const endDrag = () => {
      if (!isDragging) return
      isDragging = false
      el.classList.remove('cursor-grabbing')
      snapTo(velocity * -1000) // convert px/ms to a projected pixel delta (negative: drag right = scroll left)
    }

    const handlePointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return // touch/stylus handled natively
      if (e.button !== 0) return // left click only
      if (el.scrollWidth <= el.clientWidth) return // not overflowing

      isDragging = true
      wasDragged = false
      startX = e.clientX
      startScroll = el.scrollLeft
      lastX = e.clientX
      lastTime = e.timeStamp
      velocity = 0

      e.preventDefault() // prevent native link/image drag
      el.classList.add('cursor-grabbing')
      gsap.killTweensOf(el) // cancel any in-progress animation
    }

    const handlePointerMove = (e: PointerEvent) => {
      if (!isDragging) return

      const dx = e.clientX - startX
      const dt = e.timeStamp - lastTime
      if (dt > 0) velocity = (e.clientX - lastX) / dt // px/ms, positive = dragging right
      lastX = e.clientX
      lastTime = e.timeStamp

      el.scrollLeft = startScroll - dx
      if (Math.abs(dx) > 4) wasDragged = true

      updateActiveIndex()
      onScroll?.(el.scrollLeft)
    }

    const handlePointerUp = () => endDrag()
    const handlePointerLeave = () => endDrag()

    // Handler: prevent click propagation after a drag
    const handleClick = (e: MouseEvent) => {
      if (wasDragged) {
        e.preventDefault()
        e.stopPropagation()
        wasDragged = false
      }
    }

    // Handler: native scroll (touchpad, touch) — snap via GSAP after scrolling stops
    const snapAfterScroll = useDebounceFn(() => {
      if (!isProgrammaticScroll) snapTo()
    }, 150)

    const handleScroll = () => {
      if (isProgrammaticScroll) return
      updateActiveIndex()
      onScroll?.(el.scrollLeft)
      snapAfterScroll()
    }

    // Handler: Keydown - arrow navigation
    const handleKeydown = (e: KeyboardEvent) => {
      const items = el.children
      if (!items.length) return
      if (e.key === 'ArrowLeft' && activeIndex.value > 0) {
        scrollToItem(activeIndex.value - 1)
      } else if (e.key === 'ArrowRight' && activeIndex.value < items.length - 1) {
        scrollToItem(activeIndex.value + 1)
      }
    }

    function onResize() {
      recomputeSnapTargets()
      onSnapTargets?.(snapTargets)
      if (scrollToLast) {
        updateEndSpacer()
        amountPages.value = snapTargets.length
      } else {
        calculateReachableSnapPoints()
      }
    }

    const handleResize = useDebounceFn(onResize, 200)

    // Store event handlers directly for unmount cleanup
    el._dragHandlers = {
      pointerdown: handlePointerDown,
      pointermove: handlePointerMove,
      pointerup: handlePointerUp,
      pointerleave: handlePointerLeave,
      click: handleClick,
      scroll: handleScroll,
      keydown: handleKeydown,
      resize: handleResize,
    }

    el.addEventListener('pointerdown', handlePointerDown)
    el.addEventListener('pointermove', handlePointerMove)
    el.addEventListener('pointerup', handlePointerUp)
    el.addEventListener('pointerleave', handlePointerLeave)
    el.addEventListener('click', handleClick, true) // Capture phase
    el.addEventListener('scroll', handleScroll)
    el.addEventListener('keydown', handleKeydown)
    el.setAttribute('tabindex', '0') // Make focusable for keyboard
    window.addEventListener('resize', handleResize)
  },

  unmounted(el: DragElement) {
    gsap.killTweensOf(el)
    el.querySelector('[data-drag-scroll-spacer]')?.remove()
    if (!el._dragHandlers) return
    const { pointerdown, pointermove, pointerup, pointerleave, click, scroll, keydown, resize } = el._dragHandlers
    el.removeEventListener('pointerdown', pointerdown)
    el.removeEventListener('pointermove', pointermove)
    el.removeEventListener('pointerup', pointerup)
    el.removeEventListener('pointerleave', pointerleave)
    el.removeEventListener('click', click)
    el.removeEventListener('scroll', scroll)
    el.removeEventListener('keydown', keydown)
    window.removeEventListener('resize', resize)
    delete el._dragHandlers
  },
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('drag-scroll', dragScroll)
})
